/**
 * Deterministic demo data for the hub.
 *
 * Used when the backend isn't reachable (first dev runs, offline preview,
 * Storybook-style component browsing) and to seed the design-review pages
 * before the API handlers ship. Every series is generated from a seeded RNG
 * so refreshing the page produces the same shape and stakeholders aren't
 * confused by jitter.
 *
 * Toggle on by setting `VITE_DEMO_MODE=1` (build-time env var, any build) or
 * visiting any page with `?demo=1` in the URL (Vite dev builds only — the
 * query param is not honoured in production to prevent auth bypass).
 */

import type {
  AdminUser,
  FacilityDetail,
  FacilityRow,
  IncidentPagedResult,
  OrganizationDetail,
  OrganizationRow,
  ResourceHealth,
  UserRow,
} from '@/api/types';
import type { Series } from '@/design-system/charts/types';
import type {
  BusinessSnapshot,
  BusinessKpi,
} from '@/models/business';
import type { ResourceDetail, ResourceMetricSnapshot, ResourceTimeRange } from '@/models/resourceDetail';

// ────────────────────────────────────────────────────────────────────────
// Public query helpers
// ────────────────────────────────────────────────────────────────────────

export function isDemoModeRequested(): boolean {
  // ?demo=1 is only honoured in Vite dev mode so a production deployment cannot
  // be rendered without auth simply by appending the query string. The env var
  // path (VITE_DEMO_MODE=1, injected at build time) is the only way to get demo
  // mode in a production-mode build — operators would never set it in a real deploy.
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    // Parse the query so `?demo=1` matches but `?demo=10` / `?my-demo=1` do not.
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === '1') {
      return true;
    }
  }
  // Vite injects import.meta.env at build time.
  return import.meta.env.VITE_DEMO_MODE === '1' || import.meta.env.VITE_DEMO_MODE === 'true';
}

// ────────────────────────────────────────────────────────────────────────
// Seeded RNG — small mulberry32, deterministic across runs.
// ────────────────────────────────────────────────────────────────────────

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFromString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

// ────────────────────────────────────────────────────────────────────────
// Time helpers
// ────────────────────────────────────────────────────────────────────────

const MS_PER_MIN = 60_000;
const MS_PER_HR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HR;

/** Floor an epoch-ms timestamp to the start of its UTC day. */
function startOfDayUtc(epochMs: number): number {
  const d = new Date(epochMs);
  d.setUTCHours(0, 0, 0, 0);
  return d.getTime();
}

const RANGE_TO_BUCKETS: Record<ResourceTimeRange, { points: number; bucketMs: number }> = {
  '1h': { points: 60, bucketMs: MS_PER_MIN },
  '24h': { points: 96, bucketMs: 15 * MS_PER_MIN },
  '7d': { points: 84, bucketMs: 2 * MS_PER_HR },
  // 30d uses 6-hour buckets (120 points) for resource-detail charts where
  // sub-day granularity matters. Use the explicit `bucketMs`/`points`
  // overload for genuinely daily series — see `makeDailySeries` below.
  '30d': { points: 120, bucketMs: 6 * MS_PER_HR },
};

interface BaseSeriesOpts {
  seed: string;
  baseline: number;
  jitter: number;
  /** Optional drift from start to end (e.g. 0.2 = +20% drift over the window). */
  drift?: number;
  /** Floor the values at this number. */
  floor?: number;
  /** Inject occasional outliers (probability 0..1). */
  spikeProb?: number;
  /**
   * Wall-clock anchor for the rightmost point. Pass a single value captured
   * once per snapshot so every series in that snapshot agrees on `now`.
   */
  nowEpochMs: number;
}

type MakeSeriesOpts =
  | (BaseSeriesOpts & { range: ResourceTimeRange })
  | (BaseSeriesOpts & { points: number; bucketMs: number });

function makeSeries(opts: MakeSeriesOpts): Series {
  const { seed, baseline, jitter, drift = 0, floor = 0, spikeProb = 0, nowEpochMs } = opts;
  const { points, bucketMs } =
    'range' in opts ? RANGE_TO_BUCKETS[opts.range] : { points: opts.points, bucketMs: opts.bucketMs };
  const rng = mulberry32(seedFromString(seed));
  const values: { t: number; v: number }[] = [];
  for (let i = 0; i < points; i++) {
    const t = nowEpochMs - (points - 1 - i) * bucketMs;
    const fraction = i / (points - 1 || 1);
    const driftAdj = baseline * drift * fraction;
    const noise = (rng() - 0.5) * 2 * jitter * baseline;
    const spike = rng() < spikeProb ? jitter * baseline * (1 + rng() * 2) : 0;
    const raw = baseline + driftAdj + noise + spike;
    values.push({ t, v: Math.max(floor, raw) });
  }
  return { name: seed, points: values };
}

/**
 * Convenience: 30 daily buckets ending at `nowEpochMs`. Used by series
 * that should be genuinely day-sized (e.g. `signedDocsByDay`, KPI sparks)
 * rather than the 6-hour buckets `range:'30d'` produces.
 */
function makeDailySeries(opts: BaseSeriesOpts): Series {
  return makeSeries({ ...opts, points: 30, bucketMs: MS_PER_DAY });
}

// ────────────────────────────────────────────────────────────────────────
// Resource detail
// ────────────────────────────────────────────────────────────────────────

const APPSERVICE_REQUESTS_BASELINE = 4500;
const APPSERVICE_LATENCY_BASELINE = 220;
const APPSERVICE_ERROR_RATE_BASELINE = 0.4;
const APPSERVICE_CPU_BASELINE = 38;

const COSMOS_RU_BASELINE = 6800;
const COSMOS_LATENCY_BASELINE = 7;
const COSMOS_THROTTLED_BASELINE = 0.6;

const KV_LATENCY_BASELINE = 22;
const KV_DENY_BASELINE = 0.2;

const STATUS_BUCKETS: ResourceHealth['status'][] = ['Healthy', 'Healthy', 'Healthy', 'Degraded', 'Unhealthy'];
const CRITICALITY_BUCKETS: ResourceHealth['criticality'][] = [
  'Critical',
  'Critical',
  'Important',
  'Important',
  'Informational',
];

/**
 * Build a fake `ResourceDetail` for the given resource id + range. A single
 * UTC-midnight `today` is captured up-front so the 30-day heatmap
 * `dayStartEpochMs` cells have consistent day boundaries. Metric series use
 * `nowEpochMs` as their time anchor (wall-clock relative). `observedAt` also
 * uses `nowEpochMs` so the freshness badge reflects current data.
 */
export function demoResourceDetail(resourceId: string, range: ResourceTimeRange = '24h'): ResourceDetail {
  // nowEpochMs: wall-clock anchor for metric series and observedAt.
  // todayUtc:   UTC-midnight anchor for dayStartEpochMs heatmap cells only.
  const nowEpochMs = Date.now();
  const todayUtc = startOfDayUtc(nowEpochMs);
  const metrics = pickMetricSet(resourceId, range, nowEpochMs);
  // Construct a properly-typed ResourceHealth (no `unknown` cast) so any
  // backend rename surfaces here as a TS error.
  const observedAt = nowEpochMs;
  const idRng = mulberry32(seedFromString(`${resourceId}:meta`));
  const status = STATUS_BUCKETS[Math.floor(idRng() * STATUS_BUCKETS.length)];
  const criticality = CRITICALITY_BUCKETS[Math.floor(idRng() * CRITICALITY_BUCKETS.length)];
  const uptimePct = jitterUptime(`${resourceId}:headline`);
  const health: ResourceHealth = {
    id: resourceId,
    kind: 'Microsoft.Web/sites',
    name: resourceId,
    region: 'westus3',
    criticality,
    status,
    uptimePct,
    observedAt,
    last30Days: Array.from({ length: 30 }, (_, i) => ({
      dayStartEpochMs: todayUtc - (29 - i) * MS_PER_DAY,
      uptimePct: jitterUptime(`${resourceId}:${String(i)}`),
    })),
  };
  return {
    timeRange: range,
    portalDeepLink: null,
    health,
    metrics,
  };
}

function jitterUptime(seed: string): number {
  const rng = mulberry32(seedFromString(seed));
  const r = rng();
  // 80% of days at >= 99.95, 15% degraded, 5% bad.
  const T_HIGH = 0.8;
  const T_MID = 0.95;
  const HIGH_BASE = 99.95;
  const HIGH_RANGE = 0.05;
  const MID_BASE = 99.0;
  const MID_RANGE = 0.95;
  const LOW_BASE = 95;
  const LOW_RANGE = 4;
  if (r < T_HIGH) {
    return HIGH_BASE + rng() * HIGH_RANGE;
  }
  if (r < T_MID) {
    return MID_BASE + rng() * MID_RANGE;
  }
  return LOW_BASE + rng() * LOW_RANGE;
}

function pickMetricSet(
  resourceId: string,
  range: ResourceTimeRange,
  nowEpochMs: number,
): ResourceMetricSnapshot[] {
  if (resourceId.startsWith('cosmos')) {
    return [
      {
        key: 'totalRequests',
        label: 'Requests',
        unit: 'count',
        series: makeSeries({ seed: `${resourceId}:req`, range, baseline: COSMOS_RU_BASELINE, jitter: 0.18, nowEpochMs }),
      },
      {
        key: 'serverSideLatencyP95',
        label: 'p95 latency',
        unit: 'ms',
        tone: 'healthy',
        lowerIsBetter: true,
        series: makeSeries({
          seed: `${resourceId}:lat`,
          range,
          baseline: COSMOS_LATENCY_BASELINE,
          jitter: 0.4,
          spikeProb: 0.04,
          nowEpochMs,
        }),
      },
      {
        key: 'throttling429',
        label: 'Throttled (%)',
        unit: 'percent',
        tone: 'degraded',
        lowerIsBetter: true,
        series: makeSeries({
          seed: `${resourceId}:throt`,
          range,
          baseline: COSMOS_THROTTLED_BASELINE,
          jitter: 0.6,
          floor: 0,
          spikeProb: 0.05,
          nowEpochMs,
        }),
      },
    ];
  }
  if (resourceId.startsWith('keyvault')) {
    return [
      {
        key: 'apiLatencyMs',
        label: 'API latency',
        unit: 'ms',
        tone: 'healthy',
        lowerIsBetter: true,
        series: makeSeries({ seed: `${resourceId}:lat`, range, baseline: KV_LATENCY_BASELINE, jitter: 0.25, nowEpochMs }),
      },
      {
        key: 'denyEvents',
        label: 'Deny events',
        unit: 'count',
        tone: 'unhealthy',
        lowerIsBetter: true,
        series: makeSeries({
          seed: `${resourceId}:deny`,
          range,
          baseline: KV_DENY_BASELINE,
          jitter: 1.5,
          floor: 0,
          spikeProb: 0.08,
          nowEpochMs,
        }),
      },
    ];
  }
  // Default: App Service-style metrics
  return [
    {
      key: 'requests',
      label: 'Requests',
      unit: 'count',
      series: makeSeries({
        seed: `${resourceId}:req`,
        range,
        baseline: APPSERVICE_REQUESTS_BASELINE,
        jitter: 0.2,
        nowEpochMs,
      }),
    },
    {
      key: 'p95',
      label: 'p95 latency',
      unit: 'ms',
      tone: 'healthy',
      lowerIsBetter: true,
      series: makeSeries({
        seed: `${resourceId}:p95`,
        range,
        baseline: APPSERVICE_LATENCY_BASELINE,
        jitter: 0.3,
        spikeProb: 0.05,
        nowEpochMs,
      }),
    },
    {
      key: 'errorRate',
      label: 'Error rate',
      unit: 'percent',
      tone: 'degraded',
      lowerIsBetter: true,
      series: makeSeries({
        seed: `${resourceId}:err`,
        range,
        baseline: APPSERVICE_ERROR_RATE_BASELINE,
        jitter: 0.6,
        floor: 0,
        nowEpochMs,
      }),
    },
    {
      key: 'cpuPct',
      label: 'CPU %',
      unit: 'percent',
      series: makeSeries({
        seed: `${resourceId}:cpu`,
        range,
        baseline: APPSERVICE_CPU_BASELINE,
        jitter: 0.25,
        nowEpochMs,
      }),
    },
  ];
}

// ────────────────────────────────────────────────────────────────────────
// Business
// ────────────────────────────────────────────────────────────────────────

const ORG_COUNT = 24;
const FACILITY_COUNT = 132;
const PATIENT_COUNT = 5_840;
const ACTIVE_USER_COUNT = 412;
const DOC_COUNT_28D = 18_400;
const DAU = 246;
const MAU = 480;

const TREND_POINTS = 30;

function spark(seed: string, baseline: number, jitter: number, nowEpochMs: number): Series {
  // Daily 30-point sparklines so KPI mini-charts share the same x-axis
  // granularity as the signed-docs bar chart below.
  return makeDailySeries({ seed, baseline, jitter, floor: 0, nowEpochMs });
}

export function demoBusinessSnapshot(): BusinessSnapshot {
  // One wall-clock read per snapshot — the KPI sparkline x-axes, the
  // signed-docs bars, and the DAU/MAU trend all anchor to the same `now`
  // (passed through `makeSeries`/`makeDailySeries` rather than each call
  // re-reading the clock).
  const nowEpochMs = Date.now();
  const todayUtc = startOfDayUtc(nowEpochMs);
  const compare28d = 'vs. 28 days ago';
  const kpis: BusinessKpi[] = [
    {
      key: 'organizations',
      label: 'Organizations',
      value: ORG_COUNT,
      previousValue: ORG_COUNT - 1,
      unit: 'count',
      spark: spark('org', ORG_COUNT, 0.05, nowEpochMs),
      comparisonLabel: compare28d,
    },
    {
      key: 'facilities',
      label: 'Facilities',
      value: FACILITY_COUNT,
      previousValue: 128,
      unit: 'count',
      spark: spark('fac', FACILITY_COUNT, 0.06, nowEpochMs),
      comparisonLabel: compare28d,
    },
    {
      key: 'activeUsers',
      label: 'Active users',
      value: ACTIVE_USER_COUNT,
      previousValue: 396,
      unit: 'count',
      spark: spark('user', ACTIVE_USER_COUNT, 0.1, nowEpochMs),
      comparisonLabel: compare28d,
    },
    {
      key: 'patients',
      label: 'Active patients',
      value: PATIENT_COUNT,
      previousValue: 5_660,
      unit: 'count',
      spark: spark('pat', PATIENT_COUNT, 0.07, nowEpochMs),
      comparisonLabel: compare28d,
    },
    {
      key: 'documents',
      label: 'Documents (28d)',
      value: DOC_COUNT_28D,
      previousValue: 16_900,
      unit: 'count',
      spark: spark('doc', DOC_COUNT_28D, 0.12, nowEpochMs),
      comparisonLabel: compare28d,
    },
    {
      key: 'dau',
      label: 'DAU',
      value: DAU,
      previousValue: 232,
      unit: 'count',
      spark: spark('dau', DAU, 0.18, nowEpochMs),
      comparisonLabel: compare28d,
    },
    {
      key: 'mau',
      label: 'MAU',
      value: MAU,
      previousValue: 462,
      unit: 'count',
      spark: spark('mau', MAU, 0.05, nowEpochMs),
      comparisonLabel: compare28d,
    },
  ];
  return {
    asOf: todayUtc,
    kpis,
    // Genuinely daily — the BusinessPage card title says "daily, last 14
    // days" and slices `.points.slice(-14)`, so the points must be
    // 1-day-spaced or the slice is showing 14 6-hour buckets (~3.5d).
    signedDocsByDay: makeDailySeries({
      seed: 'signed',
      baseline: 380,
      jitter: 0.25,
      floor: 0,
      nowEpochMs,
    }),
    activityRatio: {
      name: 'DAU/MAU',
      points: Array.from({ length: TREND_POINTS }, (_, i) => ({
        t: todayUtc - (TREND_POINTS - 1 - i) * MS_PER_DAY,
        v: DAU / MAU + (mulberry32(seedFromString(`dauMau:${String(i)}`))() - 0.5) * 0.04,
      })),
    },
  };
}

export function demoOrgs(todayUtc?: number): OrganizationRow[] {
  const today = todayUtc ?? startOfDayUtc(Date.now());
  // Count facilities per org from the canonical demoFacilities() dataset so
  // the directory row's facilityCount is consistent with the detail drawer.
  const facilities = demoFacilities();
  const facilitiesPerOrg = new Map<string, number>();
  for (const f of facilities) {
    facilitiesPerOrg.set(f.organizationId, (facilitiesPerOrg.get(f.organizationId) ?? 0) + 1);
  }
  return Array.from({ length: ORG_COUNT }, (_, i) => {
    const rng = mulberry32(seedFromString(`org:${String(i)}`));
    const orgId = `org-${String(i + 1).padStart(3, '0')}`;
    return {
      organizationId: orgId,
      displayName: `${pickName(rng)} Healthcare Group`,
      facilityCount: facilitiesPerOrg.get(orgId) ?? 0,
      activeUserCount: 4 + Math.floor(rng() * 40),
      createdAtEpochMs: today - Math.floor(rng() * 600 * MS_PER_DAY),
      countsRolledUpAtMs: null,
    } satisfies OrganizationRow;
  });
}

export function demoFacilities(): FacilityRow[] {
  const states = ['CA', 'TX', 'WA', 'NY', 'FL', 'IL'] as const;
  const cities = ['San Francisco', 'Austin', 'Seattle', 'Buffalo', 'Tampa', 'Chicago'] as const;
  return Array.from({ length: FACILITY_COUNT }, (_, i) => {
    const rng = mulberry32(seedFromString(`fac:${String(i)}`));
    const orgIdx = Math.floor(rng() * ORG_COUNT);
    const stateIdx = Math.floor(rng() * states.length);
    return {
      facilityId: `fac-${String(i + 1).padStart(4, '0')}`,
      organizationId: `org-${String(orgIdx + 1).padStart(3, '0')}`,
      organizationDisplayName: `${pickName(rng)} Healthcare Group`,
      displayName: `${pickName(rng)} ${pickFacilityType(rng)}`,
      city: cities[stateIdx],
      state: states[stateIdx],
      // activePatientCount is null in M3 (FHIR-backed patient counts deferred to AB#3543)
      activePatientCount: null,
      activeUserCount: 4 + Math.floor(rng() * 40),
      countsRolledUpAtMs: null,
    } satisfies FacilityRow;
  });
}

export function demoOrg(id: string): OrganizationDetail {
  const nowEpochMs = Date.now();
  const todayUtc = startOfDayUtc(nowEpochMs);
  // Derive row fields from demoOrgs() so the drawer is consistent with the clicked row.
  // Pass todayUtc so demoOrgs shares the same clock read as this function.
  const row = demoOrgs(todayUtc).find((o) => o.organizationId === id);
  const rng = mulberry32(seedFromString(`org-detail:${id}`));
  const demoAdmin: AdminUser = {
    userId: `user-admin-${id}`,
    displayName: 'Demo Admin',
    email: 'admin@demo.local',
    status: 'active',
    lastSignInEpochMs: nowEpochMs - MS_PER_HR * 2,
  };
  // Filter demo facilities to those belonging to this org for internal consistency
  const orgFacilities = demoFacilities().filter((f) => f.organizationId === id);
  return {
    organizationId: id,
    displayName: row?.displayName ?? `${NAMES[Math.floor(rng() * NAMES.length)]} Healthcare Group`,
    dataAsOfMs: nowEpochMs - MS_PER_HR * 0.1,
    createdAtEpochMs: row?.createdAtEpochMs ?? todayUtc - Math.floor(rng() * 600 * MS_PER_DAY),
    addresses: [
      { line1: '123 Main St', line2: null, city: 'Springfield', state: 'IL', postalCode: '62701', countryCode: 'US' },
    ],
    contact: { name: 'Demo Contact', email: 'contact@demo.local', phone: '+1-555-000-0001' },
    license: { number: 'LIC-00001', state: 'IL', type: 'SNF', issuingAuthority: 'IL DHFS', validThroughEpochMs: todayUtc + 365 * MS_PER_DAY },
    counts: {
      // Derive facility counts from the actual orgFacilities list so the org
      // detail drawer is internally consistent with the facilities mini-list.
      facilitiesTotal: orgFacilities.length,
      usersTotal: row?.activeUserCount ?? 20,
      usersActive: row?.activeUserCount ?? 18,
      activePatientsLast30d: null,
      docsSignedLast7d: 35,
    },
    countsRolledUpAtMs: null,
    admins: [demoAdmin],
    facilities: orgFacilities.slice(0, 10),
    facilitiesContinuation: null,
  };
}

export function demoFacility(id: string): FacilityDetail {
  const nowEpochMs = Date.now();
  const todayUtc = startOfDayUtc(nowEpochMs);
  // Derive row fields from demoFacilities() so the drawer matches the clicked row
  const row = demoFacilities().find((f) => f.facilityId === id);
  const rng = mulberry32(seedFromString(`fac-detail:${id}`));
  const demoAdmin: AdminUser = {
    userId: `user-fadmin-${id}`,
    displayName: 'Demo Facility Admin',
    email: 'fadmin@demo.local',
    status: 'active',
    lastSignInEpochMs: nowEpochMs - MS_PER_HR * 3,
  };
  return {
    facilityId: id,
    organizationId: row?.organizationId ?? 'org-001',
    organizationDisplayName: row?.organizationDisplayName ?? 'Demo Healthcare Group',
    displayName: row?.displayName ?? `${NAMES[Math.floor(rng() * NAMES.length)]} Skilled Nursing`,
    dataAsOfMs: nowEpochMs - MS_PER_HR * 0.1,
    createdAtEpochMs: todayUtc - Math.floor(rng() * 400 * MS_PER_DAY),
    addresses: [
      { line1: '456 Oak Ave', line2: null, city: row?.city ?? 'Springfield', state: row?.state ?? 'IL', postalCode: '62702', countryCode: 'US' },
    ],
    contact: { name: 'Demo Facility Contact', email: 'facility@demo.local', phone: '+1-555-000-0002' },
    license: { number: 'LIC-F-00001', state: row?.state ?? 'IL', type: 'SNF', issuingAuthority: 'IL DHFS', validThroughEpochMs: todayUtc + 300 * MS_PER_DAY },
    counts: {
      usersTotal: row?.activeUserCount ?? 8,
      usersActive: row?.activeUserCount ?? 7,
      activePatientsLast30d: null,
      docsCreatedLast7d: 12,
      docsSignedLast7d: 10,
      lastActivityEpochMs: nowEpochMs - MS_PER_HR,
    },
    countsRolledUpAtMs: null,
    admins: [demoAdmin],
  };
}

const NAMES = [
  'Ironwood', 'Juniper', 'Kapok', 'Larch', 'Magnolia', 'Norway', 'Oak',
  'Pine', 'Quince', 'Redwood', 'Sequoia', 'Tamarack', 'Umbrella', 'Vine',
  'Willow', 'Xanthe', 'Yew', 'Zelkova',
];
function pickName(rng: () => number): string {
  return NAMES[Math.floor(rng() * NAMES.length)];
}

const FACILITY_TYPES = ['Care Center', 'Clinic', 'Skilled Nursing', 'Rehab', 'Outpatient'];
function pickFacilityType(rng: () => number): string {
  return FACILITY_TYPES[Math.floor(rng() * FACILITY_TYPES.length)];
}

// ────────────────────────────────────────────────────────────────────────
// Incidents demo data
// ────────────────────────────────────────────────────────────────────────

export function demoIncidents(): IncidentPagedResult {
  // Capture `now` inside the function so relative timestamps stay fresh across
  // repeated calls (e.g. TanStack Query refetches while the app is open).
  const now = Date.now();
  return {
    continuation: null,
    items: [
      {
        id: 'demo-inc-001',
        env: 'prod',
        alertRuleId: '/subscriptions/4734f808/providers/microsoft.insights/alertrules/high-rcu-cosmos',
        alertRuleName: 'Cosmos DB high RU consumption',
        severity: 0,
        signalType: 'Metric',
        monitorService: 'Platform',
        description: 'Normalized RU consumption exceeded 85% for 10 minutes',
        alertState: 'New',
        monitorCondition: 'Fired',
        targetResourceName: 'cdb-caredocsai-prod',
        targetResourceType: 'Microsoft.DocumentDB/databaseAccounts',
        firedAtMs: now - MS_PER_HR / 2,
        resolvedAtMs: null,
        lastUpdatedAtMs: now - MS_PER_HR / 2,
      },
      {
        id: 'demo-inc-002',
        env: 'prod',
        alertRuleId: '/subscriptions/4734f808/providers/microsoft.insights/scheduledqueryrules/failed-deps',
        alertRuleName: 'App Service failed dependency calls',
        severity: 1,
        signalType: 'Log',
        monitorService: 'Log Analytics',
        description: 'More than 5 failed dependency calls in 5 minutes',
        alertState: 'Acknowledged',
        monitorCondition: 'Fired',
        targetResourceName: 'app-caredocsai-prod-api',
        targetResourceType: 'Microsoft.Web/sites',
        firedAtMs: now - MS_PER_HR * 2,
        resolvedAtMs: null,
        lastUpdatedAtMs: now - MS_PER_HR * 1.5,
      },
      {
        id: 'demo-inc-003',
        env: 'staging',
        alertRuleId: '/subscriptions/e0f03d08/providers/microsoft.insights/alertrules/kv-errors',
        alertRuleName: 'Key Vault service API hits',
        severity: 2,
        signalType: 'Metric',
        monitorService: 'Platform',
        description: 'Key Vault API hit count anomaly',
        alertState: 'New',
        monitorCondition: 'Fired',
        targetResourceName: 'kv-caredocsai-staging',
        targetResourceType: 'Microsoft.KeyVault/vaults',
        firedAtMs: now - MS_PER_HR * 4,
        resolvedAtMs: null,
        lastUpdatedAtMs: now - MS_PER_HR * 4,
      },
      {
        id: 'demo-inc-004',
        env: 'dev',
        alertRuleId: '/subscriptions/013dba41/providers/microsoft.insights/alertrules/5xx-rate',
        alertRuleName: 'App Service 5xx error rate',
        severity: 1,
        signalType: 'Metric',
        monitorService: 'Platform',
        description: 'HTTP 5xx rate exceeded 2% over 5 minutes',
        alertState: 'Closed',
        monitorCondition: 'Resolved',
        targetResourceName: 'app-caredocsai-dev-api',
        targetResourceType: 'Microsoft.Web/sites',
        firedAtMs: now - MS_PER_HR * 8,
        resolvedAtMs: now - MS_PER_HR * 7,
        lastUpdatedAtMs: now - MS_PER_HR * 7,
      },
      {
        id: 'demo-inc-005',
        env: 'prod',
        alertRuleId: '/subscriptions/4734f808/providers/microsoft.insights/alertrules/cosmos-throttle',
        alertRuleName: 'Cosmos DB throttled requests',
        severity: 2,
        signalType: 'Metric',
        monitorService: 'Platform',
        description: 'Throttled requests count > 10',
        alertState: 'Closed',
        monitorCondition: 'Resolved',
        targetResourceName: 'cdb-caredocsai-prod',
        targetResourceType: 'Microsoft.DocumentDB/databaseAccounts',
        firedAtMs: now - MS_PER_HR * 24,
        resolvedAtMs: now - MS_PER_HR * 22,
        lastUpdatedAtMs: now - MS_PER_HR * 22,
      },
      {
        id: 'demo-inc-006',
        env: 'staging',
        alertRuleId: '/subscriptions/e0f03d08/providers/microsoft.insights/alertrules/cpu-high',
        alertRuleName: 'App Service CPU > 80%',
        severity: 3,
        signalType: 'Metric',
        monitorService: 'Platform',
        description: 'CPU percentage exceeded 80% for 15 minutes',
        alertState: 'Closed',
        monitorCondition: 'Resolved',
        targetResourceName: 'app-caredocsai-staging-web',
        targetResourceType: 'Microsoft.Web/sites',
        firedAtMs: now - MS_PER_HR * 48,
        resolvedAtMs: now - MS_PER_HR * 46,
        lastUpdatedAtMs: now - MS_PER_HR * 46,
      },
    ],
  };
}

// ────────────────────────────────────────────────────────────────────────
// Users demo data
// ────────────────────────────────────────────────────────────────────────

const FIRST_NAMES = ['Alice', 'Bob', 'Carol', 'David', 'Emma', 'Frank', 'Grace', 'Henry', 'Isabel', 'James', 'Karen', 'Liam'];
const LAST_NAMES = ['Anderson', 'Brown', 'Chen', 'Davis', 'Evans', 'Foster', 'Garcia', 'Hill', 'Ingram', 'Jones', 'Kim', 'Lee'];

export function demoUsers(): UserRow[] {
  return Array.from({ length: 40 }, (_, i) => {
    const rng = mulberry32(seedFromString(`user:${String(i)}`));
    const firstName = FIRST_NAMES[Math.floor(rng() * FIRST_NAMES.length)];
    const lastName = LAST_NAMES[Math.floor(rng() * LAST_NAMES.length)];
    return {
      userId: `user-${String(i + 1).padStart(4, '0')}`,
      displayName: `${firstName} ${lastName}`,
      emailAddress: `${firstName.toLowerCase()}.${lastName.toLowerCase()}${String(i + 1)}@demo.caredocs.ai`,
      status: rng() > 0.1 ? 'active' : 'unclaimed',
    } satisfies UserRow;
  });
}
